import { useMemo, useState, type FormEvent } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle, Groups } from '@mui/icons-material';
import { acceptFriendlyStudyInvitation, previewFriendlyStudyInvitation } from '@/api/friendlyStudies';
import { useAuth } from '@/auth/useAuth';
import { AuthShell } from '@/components/auth/AuthShell';
import { FRIENDLY_STUDY_BENEFIT_DURATION, FRIENDLY_STUDY_BENEFIT_LABEL, FRIENDLY_STUDY_DISPLAY_PLAN } from '@/lib/friendlyStudyBenefit';

export const Route = createFileRoute('/_public/estudios-amigos/aceptar')({
  validateSearch: (search): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: FriendlyStudyAcceptPage,
});

function FriendlyStudyAcceptPage(): JSX.Element {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [contactName, setContactName] = useState('');
  const [password, setPassword] = useState('');
  const [accepted, setAccepted] = useState(false);

  const preview = useQuery({
    queryKey: ['friendly-study-preview', token],
    queryFn: () => previewFriendlyStudyInvitation(token!),
    enabled: Boolean(token),
    retry: false,
  });

  const email = preview.data?.invitation.email ?? '';
  const fallbackName = preview.data?.invitation.contactName ?? preview.data?.invitation.studyName ?? '';
  const displayName = useMemo(() => contactName.trim() || fallbackName, [contactName, fallbackName]);
  const existingAccount = preview.data?.existingAccount === true;

  const accept = useMutation({
    mutationFn: async () => {
      const result = await acceptFriendlyStudyInvitation({ token: token!, password, contactName: displayName });
      if (!result.existingAccountReused && !result.wasAlreadyAccepted) {
        await login(result.firstAdmin.email, password, result.tenant.id);
      }
      return result;
    },
    onSuccess: async (result) => {
      setAccepted(true);
      await navigate({ to: result.existingAccountReused || result.wasAlreadyAccepted ? '/login' : '/dashboard' });
    },
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!token || (!existingAccount && password.length < 8) || !displayName.trim()) return;
    accept.mutate();
  }

  return (
    <AuthShell>
      <Card sx={{ width: '100%', maxWidth: 520, mx: 'auto' }}>
        <CardContent>
          <Stack spacing={3}>
            <Stack spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                }}
              >
                <Groups />
              </Box>
              <Typography variant="h4" fontWeight={800}>Aceptar invitación</Typography>
              <Typography color="text.secondary">
                Programa Estudios Amigos para estudios contables que prueban Tilde con acompañamiento directo.
              </Typography>
            </Stack>

            {!token ? <Alert severity="error">El enlace no incluye token de invitación.</Alert> : null}
            {preview.isLoading ? <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={20} /><Typography>Validando invitación…</Typography></Stack> : null}
            {preview.error ? <Alert severity="error">No pudimos validar la invitación. Pedí que te reenvíen el enlace.</Alert> : null}

            {preview.data ? (
              <Stack spacing={2}>
                <Alert severity="success" icon={<CheckCircle fontSize="inherit" />}>
                  Invitación válida para <strong>{preview.data.invitation.studyName}</strong>.
                </Alert>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={FRIENDLY_STUDY_DISPLAY_PLAN} color="primary" />
                  <Chip label={FRIENDLY_STUDY_BENEFIT_LABEL} color="success" />
                  <Chip label={FRIENDLY_STUDY_BENEFIT_DURATION} variant="outlined" />
                </Stack>
                <Typography variant="body2" color="text.secondary">{preview.data.benefitSummary}</Typography>
                {existingAccount ? (
                  <Alert severity="info">
                    Este email ya tiene una cuenta Tilde. Vamos a agregar Estudio Contable Free y la bonificación a esa misma cuenta; después ingresá con tus credenciales habituales.
                  </Alert>
                ) : null}

                <Box component="form" onSubmit={submit}>
                  <Stack spacing={2}>
                    <TextField label="Email" value={email} disabled fullWidth />
                    <TextField
                      label="Nombre del administrador"
                      value={contactName || fallbackName}
                      onChange={(e) => setContactName(e.target.value)}
                      fullWidth
                      required
                    />
                    {!existingAccount ? (
                      <TextField
                        label="Contraseña"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        helperText="Mínimo 8 caracteres, con letras y números."
                        fullWidth
                        required
                      />
                    ) : null}
                    {accept.error ? <Alert severity="error">No pudimos aceptar la invitación. Revisá la contraseña o pedí un nuevo enlace.</Alert> : null}
                    {accepted ? <Alert severity="success">Beneficio aplicado. Entrando a Tilde…</Alert> : null}
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={accept.isPending || !displayName.trim() || (!existingAccount && password.length < 8)}
                    >
                      {accept.isPending
                        ? 'Aplicando beneficio…'
                        : existingAccount
                          ? 'Agregar beneficio a mi cuenta'
                          : 'Crear cuenta bonificada'}
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    </AuthShell>
  );
}
